import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone,
  MessageSquare,
  Clock,
  Loader2,
  CheckCircle,
  Send,
  User,
  PhoneOff
} from 'lucide-react';
import { useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';

interface SupportVideoSessionProps {
  roomId: string;
  participantName: string;
  participantType: 'agent' | 'customer';
  ticketId: string;
  ticketDetails?: {
    subject: string;
    category: string;
    priority: string;
    customerName?: string;
    agentName?: string;
  };
  onEndSession?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'agent' | 'customer' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'system';
}

// Inner component that uses RealtimeKit hooks (same pattern as Legal template)
const SupportMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  participantName: string;
  participantType: 'agent' | 'customer';
  roomId: string;
  ticketId: string;
  ticketDetails?: any;
}> = ({ authToken, onEndCall, participantName, participantType, roomId, ticketId, ticketDetails }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [newMessage, setNewMessage] = useState('');
  
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: `Support session started for ticket #${ticketId}`,
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize meeting (same pattern as Legal template)
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[SupportMeetingComponent] Initializing with authToken:', authToken ? 'exists' : 'none');
        console.log('[SupportMeetingComponent] Room ID:', roomId);
        console.log('[SupportMeetingComponent] Participant:', participantName, 'Type:', participantType);
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[SupportMeetingComponent] Meeting initialized successfully');
        
      } catch (error) {
        console.error('[SupportMeetingComponent] Failed to initialize meeting:', error);
        alert(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease refresh the page and try again.`);
      }
    };

    if (authToken && !isInitialized) {
      init();
    }
  }, [authToken, initMeeting, isInitialized]);

  // Timer for session time
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: participantType,
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  // Handle end call
  const handleEndCall = async () => {
    console.log('[SupportMeetingComponent] End call requested, performing cleanup...');
    
    if (meeting && isInitialized) {
      try {
        await meeting.leave();
        console.log('[SupportMeetingComponent] Successfully left video meeting');
      } catch (error) {
        console.warn('[SupportMeetingComponent] Error leaving meeting:', error);
      }
    }
    
    onEndCall();
  };

  if (!meeting || !isInitialized) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Support Session</h1>
          <h2 className="text-blue-200 text-xl mb-4">{participantType === 'agent' ? 'Agent' : 'Customer'} Portal</h2>
          <p className="text-gray-300 text-lg">Connecting to support session...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          <p className="text-gray-400 text-sm">Ticket: #{ticketId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold">Support Session</h2>
            <p className="text-sm text-gray-300">
              {participantType === 'agent' ? `Customer: ${ticketDetails?.customerName || 'Customer'}` : `Agent: ${ticketDetails?.agentName || 'Support Agent'}`}
            </p>
            <p className="text-xs text-gray-400">Ticket #{ticketId}</p>
          </div>
          {ticketDetails && (
            <div className="bg-blue-900 text-blue-200 px-3 py-1 rounded-full text-xs flex items-center">
              {ticketDetails.subject}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer */}
          <div className="flex items-center text-lg font-mono">
            <Clock size={16} className="mr-2" />
            {formatTime(sessionTime)}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area with RealtimeKit */}
        <div className="flex-1 relative bg-black">
          <div className="h-full w-full relative">
            <RtkMeeting 
              meeting={meeting} 
              showSetupScreen={false}
              mode="fill"
            />
            {/* Overlay to hide any built-in controls at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-black opacity-75 pointer-events-none"></div>
          </div>

          {/* Controls Overlay */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-4 py-2 flex items-center space-x-3">
              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-2 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={18} />
              </button>

              {/* End Call */}
              <button
                onClick={handleEndCall}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <PhoneOff size={18} />
              </button>
            </div>
          </div>

          {/* Meeting info overlay */}
          <div className="absolute top-4 left-4 z-50 bg-black bg-opacity-50 text-white p-3 rounded-lg">
            <h3 className="font-semibold">{participantName}</h3>
            <p className="text-sm text-gray-300">Support Session</p>
            <p className="text-xs text-gray-400 mt-1">{participantType} • Connected</p>
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Session Chat</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${
                    msg.sender === 'system' 
                      ? 'text-center' 
                      : msg.sender === participantType 
                        ? 'ml-auto' 
                        : 'mr-auto'
                  }`}
                >
                  {msg.sender === 'system' ? (
                    <div className="bg-blue-50 text-blue-800 text-xs px-3 py-2 rounded-lg">
                      {msg.message}
                    </div>
                  ) : (
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                        msg.sender === participantType
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type a message..."
                />
                <button
                  onClick={sendMessage}
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const SupportVideoSession: React.FC<SupportVideoSessionProps> = ({
  roomId,
  participantName,
  participantType,
  ticketId,
  ticketDetails,
  onEndSession
}) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEndedMessage, setShowEndedMessage] = useState(false);

  // Initialize token when component mounts
  useEffect(() => {
    const initializeToken = async () => {
      if (!roomId || !participantName) {
        setError('Missing room ID or participant name');
        setIsInitializing(false);
        return;
      }

      try {
        console.log('[SupportVideoSession] Generating token for support session:', {
          roomId,
          participantName,
          participantType,
          ticketId
        });
        
        // Use debug endpoint (same as Legal template)
        const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            meetingId: roomId,
            participantName: participantName,
            isHost: participantType === 'agent'
          })
        });

        if (response.ok) {
          const tokenData = await response.json();
          setAuthToken(tokenData.token);
          console.log('[SupportVideoSession] Token generated successfully');
        } else {
          throw new Error(`Failed to generate token: ${response.status}`);
        }
        
        setIsInitializing(false);

      } catch (error) {
        console.error('[SupportVideoSession] Failed to initialize token:', error);
        setError(error instanceof Error ? error.message : 'Failed to join support session');
        setIsInitializing(false);
      }
    };

    initializeToken();
  }, [roomId, participantName, participantType, ticketId]);

  // Handle end call
  const handleEndCall = async () => {
    try {
      console.log('[SupportVideoSession] Ending call cleanup...');
      setShowEndedMessage(true);
    } catch (error) {
      console.error('[SupportVideoSession] Error during call cleanup:', error);
    }
  };

  // Show ended message
  if (showEndedMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Ended</h2>
          <p className="text-gray-600 mb-6">
            Thank you for using our support. We hope we were able to help resolve your issue.
          </p>
          <button
            onClick={() => onEndSession ? onEndSession() : window.close()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isInitializing || !authToken) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Support Session</h1>
          <h2 className="text-blue-200 text-xl mb-4">{participantType === 'agent' ? 'Agent' : 'Customer'} Portal</h2>
          <p className="text-gray-300 text-lg">Joining session...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          <p className="text-gray-400 text-sm">Ticket: #{ticketId}</p>
          {error && (
            <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-900 via-gray-900 to-red-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl mb-2">Session Error</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Render the RealtimeKit meeting
  return (
    <SupportMeetingComponent
      authToken={authToken}
      onEndCall={handleEndCall}
      participantName={participantName}
      participantType={participantType}
      roomId={roomId}
      ticketId={ticketId}
      ticketDetails={ticketDetails}
    />
  );
};